import type { Role } from '../types/constants';

/**
 * Roles an admin composes, expressed as capabilities.
 *
 * Authorisation in this product reads no documents (CLAUDE.md §5.1), so a
 * role an admin invented cannot be a name the rules look up — it has to
 * arrive in the claim. The claim therefore carries a second map beside
 * `orgs`, holding the capability codes for anyone on a custom role:
 *
 *   { orgs: { "<orgId>": "custom" }, caps: { "<orgId>": ["la","ra"] } }
 *
 * Codes rather than readable names because the claim payload caps at 1000
 * bytes, and that ceiling is already what bounds how many workspaces one
 * person can belong to.
 *
 * A built-in role behaves exactly as it always has and costs no claim bytes:
 * `capabilitiesOf` expands it here, on the client, only so screens can ask
 * one question instead of two. The rules keep their own copy of the same
 * expansion, because rules cannot import TypeScript. `tests/rules` is what
 * holds the two in agreement.
 */

export const CAPABILITIES = [
  'wm', // work.manage
  'pm', // people.manage
  'la', // leave.approveAny
  'lb', // leave.allowances
  'ra', // reports.all
  'tv', // time.viewAll
  'te', // time.editAny
  'fr', // finance.read
  'fw', // finance.write
  'sm', // settings.manage
  'ar', // audit.read
  'ex', // export.full
  'cl', // clientLinks
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export interface CapabilitySpec {
  code: Capability;
  /** The i18n key stem under `roles.cap`. */
  key: string;
  /**
   * The built-in roles that hold this today. Shown in the picker so an admin
   * can reason by comparison — "this is what a manager can do" — instead of
   * guessing from a label.
   */
  heldBy: readonly Role[];
  /** Granting this without its dependency produces a role that cannot use it. */
  needs?: Capability;
}

export const CAPABILITY_SPECS: readonly CapabilitySpec[] = [
  { code: 'wm', key: 'workManage', heldBy: ['owner', 'admin', 'manager'] },
  { code: 'pm', key: 'peopleManage', heldBy: ['owner', 'admin'] },
  { code: 'la', key: 'leaveApprove', heldBy: ['owner', 'admin'] },
  { code: 'lb', key: 'leaveAllowances', heldBy: ['owner', 'admin'] },
  { code: 'ra', key: 'reportsAll', heldBy: ['owner', 'admin', 'manager'] },
  { code: 'tv', key: 'timeViewAll', heldBy: ['owner', 'admin', 'manager'] },
  { code: 'te', key: 'timeEditAny', heldBy: ['owner', 'admin'], needs: 'tv' },
  { code: 'fr', key: 'financeRead', heldBy: ['owner', 'admin', 'accountant'] },
  { code: 'fw', key: 'financeWrite', heldBy: ['owner', 'admin', 'accountant'], needs: 'fr' },
  { code: 'sm', key: 'settingsManage', heldBy: ['owner', 'admin'] },
  { code: 'ar', key: 'auditRead', heldBy: ['owner', 'admin'] },
  { code: 'ex', key: 'exportFull', heldBy: ['owner', 'admin'] },
  { code: 'cl', key: 'clientLinks', heldBy: ['owner', 'admin', 'manager'] },
];

const SPEC_BY_CODE = new Map(CAPABILITY_SPECS.map((s) => [s.code, s]));

export function isCapability(value: unknown): value is Capability {
  return typeof value === 'string' && SPEC_BY_CODE.has(value as Capability);
}

/**
 * What a built-in role holds, derived from the same table the picker shows.
 *
 * Owner and admin are deliberately absent as *composable* things — they are
 * not customisable — but they expand here so a screen can ask
 * `can(role, caps, 'fr')` once rather than branching on the role first.
 */
export function capabilitiesOf(role: Role, custom: readonly string[] = []): Set<Capability> {
  if (role === 'custom') {
    return new Set(custom.filter(isCapability));
  }
  return new Set(CAPABILITY_SPECS.filter((s) => s.heldBy.includes(role)).map((s) => s.code));
}

export function can(role: Role, custom: readonly string[] | undefined, capability: Capability): boolean {
  return capabilitiesOf(role, custom ?? []).has(capability);
}

/**
 * A requested capability list, made safe.
 *
 * Unknown codes are dropped rather than stored, because a code the rules do
 * not know is a permission nobody can reason about. A capability whose
 * dependency is missing brings it along: editing anyone's time without being
 * able to see anyone's time is a role that appears to work and does not.
 */
export function cleanCapabilities(requested: readonly unknown[] | null | undefined): Capability[] {
  const set = new Set<Capability>();
  for (const value of requested ?? []) if (isCapability(value)) set.add(value);
  for (const code of [...set]) {
    const needs = SPEC_BY_CODE.get(code)?.needs;
    if (needs) set.add(needs);
  }
  // Stored in the table's order, so two identical roles compare equal.
  return CAPABILITIES.filter((code) => set.has(code));
}

export interface RolePreset {
  id: string;
  /** i18n key stem under `roles.preset`. */
  key: string;
  capabilities: readonly Capability[];
}

/**
 * Where the picker opens.
 *
 * An empty grid of thirteen switches is how somebody builds a role that
 * cannot open the application. Each of these is a starting point that is then
 * edited, the same way the theme presets work (D48).
 */
export const ROLE_PRESETS: readonly RolePreset[] = [
  { id: 'hr', key: 'hr', capabilities: ['la', 'lb', 'ra'] },
  { id: 'projectLead', key: 'projectLead', capabilities: ['wm', 'ra', 'tv', 'cl'] },
  { id: 'bookkeeper', key: 'bookkeeper', capabilities: ['fr', 'ex'] },
  { id: 'officeManager', key: 'officeManager', capabilities: ['pm', 'lb', 'sm'] },
  { id: 'auditor', key: 'auditor', capabilities: ['ra', 'tv', 'ar', 'ex'] },
];
